import * as React from 'react';
import * as SignalR from '@microsoft/signalr';
import { datadogLogs } from '@datadog/browser-logs';

import {
  CassieSendLogContext,
  IntegrationChannel,
  NotifyConversationClosedPayload,
  NotifyConversationOpenedPayload,
  OutgoingEventPromiseType,
  OutgoingThirdPartyConnectorEventPayloads,
  ThirdPartyWSMethod
} from './types';

import config from '../../../config';
import useSignalR, { START_WS_RETRY_DELAY } from './useSignalR';

export type CassieWebsocketInvokeMethod = <
  T extends keyof OutgoingThirdPartyConnectorEventPayloads & keyof OutgoingEventPromiseType
>(
  method: T,
  payload?: OutgoingThirdPartyConnectorEventPayloads[T],
  logContext?: CassieSendLogContext
) => Promise<OutgoingEventPromiseType[T]>;

interface ContextInterface {
  connection: SignalR.HubConnection | undefined;
  invoke: CassieWebsocketInvokeMethod;
  connectionState: SignalR.HubConnectionState;
}

const CassieWebsocketContext: React.Context<ContextInterface> = React.createContext<ContextInterface>({
  connection: undefined,
  invoke: async <T extends keyof OutgoingThirdPartyConnectorEventPayloads & keyof OutgoingEventPromiseType>(): Promise<
    OutgoingEventPromiseType[T]
  > => Promise.resolve() as Promise<OutgoingEventPromiseType[T]>,
  connectionState: SignalR.HubConnectionState.Disconnected
});

const CassieWebsocketContextProvider: React.FC<{
  children: React.ReactNode;
  thirdParty?: IntegrationChannel;
}> = ({ children }): React.ReactElement => {
  const [userEmail, setUserEmail] = React.useState<string | undefined>();
  const [agentId, setAgentId] = React.useState<string | undefined>();
  const [connection, setConnection] = React.useState<SignalR.HubConnection>();

  // The connection (by its connectedTimes) the hub acknowledged the subscription on
  const [subscribedOn, setSubscribedOn] = React.useState<number>();
  // Whether the hub acknowledged the subscription on the live connection. A ref, cleared by onclose, so a send
  // from a callback rendered before a drop and reconnect can't go out before the new subscription
  const isSubscribed = React.useRef(false);
  // Bumped to subscribe again after the hub rejected a subscription
  const [subscribeRetries, setSubscribeRetries] = React.useState(0);

  // The latest conversation opened and not closed since, sent again after every (re)subscription
  const latestOpen = React.useRef<NotifyConversationOpenedPayload>();
  // A close the hub hasn't accepted (dropped while disconnected, or rejected), sent once subscribed so Cassie
  // doesn't keep showing a conversation that has ended
  const pendingClose = React.useRef<NotifyConversationClosedPayload>();
  // The latest open or close sent on the current subscription, so the resend doesn't send it a second time
  const sentOnSubscription = React.useRef<NotifyConversationOpenedPayload | NotifyConversationClosedPayload>();

  React.useEffect(() => {
    connect.agent(function (agent) {
      const email = agent.getConfiguration().username;
      setUserEmail(email);
      setAgentId(agent.getAgentARN().split('/').pop());
    });
  }, []);

  // This hook is used to build the websocket connection (using the context connection to keep a unique instance)
  // It starts once the user email is set, and restarts itself after a drop
  const {
    connection: wsConnection,
    connectionState,
    connectedTimes
  } = useSignalR({
    connectionName: 'Cassie',
    url: config.cassieWebsocketURL,
    contextConnection: connection,
    autoConnect: Boolean(userEmail)
  });

  // Keep track if the websocket is connected
  const isConnectionEstablished = connection && connectionState === SignalR.HubConnectionState.Connected && userEmail;
  // Subscribed on the current connection: after a reconnect Cato has to subscribe again
  const hasSubscribed = Boolean(isConnectionEstablished) && subscribedOn === connectedTimes;

  // Stores the connection in the context (to maintain a unique connection instance between renders)
  React.useEffect(() => {
    setConnection(wsConnection);
  }, [wsConnection]);

  React.useEffect(() => {
    connection?.onclose(() => {
      isSubscribed.current = false;
    });
  }, [connection]);

  // Wrapper for the WS invoke method from the websocket connection binding the user email
  const invoke: CassieWebsocketInvokeMethod = React.useCallback(
    async (method, payload?, logContext?) => {
      const isOpen = method === ThirdPartyWSMethod.NOTIFY_CONVERSATION_OPENED;
      const isClose = method === ThirdPartyWSMethod.NOTIFY_CONVERSATION_CLOSED;

      // Every send is logged with its outcome, so it can be followed from Cato to Cassie (CSD-3538). The contact
      // is the one an open is about, or the one on screen for a focus or close
      const contactId = isOpen
        ? (payload as NotifyConversationOpenedPayload).channelConversationId
        : isClose || method === ThirdPartyWSMethod.NOTIFY_THIRD_PARTY_FOCUSED
          ? latestOpen.current?.channelConversationId
          : undefined;
      const logSend = (outcome: 'sent' | 'failed' | 'dropped'): void => {
        datadogLogs.logger.info(`WEBSOCKET event sent to Cassie`, {
          event: method,
          outcome,
          contactId,
          agentId,
          ...logContext
        });
      };
      if (isOpen) {
        latestOpen.current = payload as NotifyConversationOpenedPayload;
        pendingClose.current = undefined;
      }
      if (isClose) {
        latestOpen.current = undefined;
        pendingClose.current = payload as NotifyConversationClosedPayload;
      }

      // Only subscribing goes out before the subscription is acknowledged, so an open isn't sent twice. Checked
      // against the live connection, not this render's, which can predate a drop and reconnect
      const isConnected = connection?.state === SignalR.HubConnectionState.Connected && userEmail;
      if (isConnected && (isSubscribed.current || method === ThirdPartyWSMethod.SUBSCRIBE)) {
        if (isOpen || isClose)
          sentOnSubscription.current = payload as NotifyConversationOpenedPayload | NotifyConversationClosedPayload;
        // If connection is established, directly send the invoke
        const promise: Promise<OutgoingEventPromiseType[typeof method]> = payload
          ? connection.invoke(method, userEmail, payload)
          : connection.invoke(method, userEmail);

        // Return the promise for further chaining
        return promise.then(
          result => {
            if (isClose && pendingClose.current === payload) pendingClose.current = undefined;
            logSend('sent');
            return result;
          },
          (err: Error) => {
            logSend('failed');
            const errorMessage = payload
              ? `Error when invoking ${method} with payload`
              : `Error when invoking ${method}`;
            datadogLogs.logger.error(`${errorMessage}`, { integration: 'cassie', standalone: true }, err);

            throw err;
          }
        );
      }
      // If not subscribed, drop the invoke: replaying a queue of them resends stale opens and closes.
      // The latest open, or a close that didn't get through, is sent once subscribed
      logSend('dropped');
      return undefined as OutgoingEventPromiseType[typeof method];
    },
    [connection, userEmail, agentId]
  );

  // This makes sure to subscribe to the websocket when the connection is established
  const handleOnConnected = React.useCallback(() => {
    sentOnSubscription.current = undefined;
    void invoke(ThirdPartyWSMethod.SUBSCRIBE, { thirdPartyName: IntegrationChannel.CATO }).then(
      () => {
        // A drop since rejects the subscribe, so this acknowledgement is for the live connection
        isSubscribed.current = true;
        setSubscribedOn(connectedTimes);
      },
      // Every send is dropped until subscribed, so retry. Subscribing twice is harmless: the hub only adds Cato to a group
      () => setTimeout(() => setSubscribeRetries(retries => retries + 1), START_WS_RETRY_DELAY)
    );
  }, [connectedTimes, invoke]);

  // Once (re)subscribed, send the conversation on screen again, or the close of one that ended, as Cassie may have
  // missed it while Cato was disconnected
  React.useEffect(() => {
    if (!hasSubscribed) return;
    // A consumer effect may already have sent it, when its change was committed with the acknowledgement
    if ((latestOpen.current ?? pendingClose.current) === sentOnSubscription.current) return;
    const resend = latestOpen.current
      ? invoke(ThirdPartyWSMethod.NOTIFY_CONVERSATION_OPENED, latestOpen.current, { resent: true })
      : pendingClose.current &&
        invoke(ThirdPartyWSMethod.NOTIFY_CONVERSATION_CLOSED, pendingClose.current, { resent: true });
    // A failed send needs no handling: it is kept and sent again after the next subscription
    resend?.catch(() => undefined);
  }, [hasSubscribed, invoke]);

  // Handle the onConnected event (first connection / reconnection)
  React.useEffect(() => {
    if (!hasSubscribed && isConnectionEstablished) {
      handleOnConnected();
    }
  }, [isConnectionEstablished, hasSubscribed, handleOnConnected, subscribeRetries]);

  // Stop the connection when the context is unmounted
  React.useEffect(() => {
    return () => {
      void connection?.stop();
    };
  }, [connection]);

  return (
    <CassieWebsocketContext.Provider
      value={{
        connection,
        connectionState,
        invoke
      }}
    >
      {children}
    </CassieWebsocketContext.Provider>
  );
};

export { CassieWebsocketContext, CassieWebsocketContextProvider };

// Hook for using the CassieWebsocketContext
export const useCassieWebsocket = (): ContextInterface => React.useContext(CassieWebsocketContext);
